import { useState, useEffect, useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Save,
  Download,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  Search,
  X,
  AlertTriangle,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  StockTake,
  StockTakeItem,
  StockTakeStatus,
  calculateProgress,
  getStatusColor,
  getTypeColor,
  getNextStatus,
  getPreviousStatus,
} from "@/data/mockStockTakes";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Helper to safely format dates (handles JSON-serialized date strings)
const formatDateSafe = (date: Date | string | null | undefined): string => {
  if (!date) return "-";
  const d = date instanceof Date ? date : new Date(date);
  return isNaN(d.getTime()) ? "-" : d.toLocaleDateString();
};

const formatDateTimeSafe = (date: Date | string | null | undefined): string => {
  if (!date) return "-";
  const d = date instanceof Date ? date : new Date(date);
  return isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

interface StockTakeDetailsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stockTake: StockTake | null;
  onSave: (stockTake: StockTake) => void;
  onExport: (stockTake: StockTake, format: "csv" | "excel") => void;
}

export function StockTakeDetailsDrawer({
  open,
  onOpenChange,
  stockTake,
  onSave,
  onExport,
}: StockTakeDetailsDrawerProps) {
  const [editedStockTake, setEditedStockTake] = useState<StockTake | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [varianceFilter, setVarianceFilter] = useState<string>("all");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showStatusChangeDialog, setShowStatusChangeDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<StockTakeStatus | null>(null);

  useEffect(() => {
    if (stockTake) {
      setEditedStockTake(JSON.parse(JSON.stringify(stockTake)));
      setSearchQuery("");
      setVarianceFilter("all");
      setHasUnsavedChanges(false);
    }
  }, [stockTake]);

  // Filter items - must be called before early return to maintain hooks order
  const filteredItems = useMemo(() => {
    if (!editedStockTake) return [];
    let filtered = editedStockTake.items;

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.stockCode.toLowerCase().includes(query) ||
          item.stockDescription.toLowerCase().includes(query) ||
          item.location.toLowerCase().includes(query)
      );
    }

    // Apply variance filter
    if (varianceFilter === "with-variance") {
      filtered = filtered.filter((item) => item.variance !== null && item.variance !== 0);
    } else if (varianceFilter === "uncounted") {
      filtered = filtered.filter((item) => item.countedQuantity === null);
    } else if (varianceFilter === "counted") {
      filtered = filtered.filter((item) => item.countedQuantity !== null);
    }

    return filtered;
  }, [editedStockTake, searchQuery, varianceFilter]);

  if (!editedStockTake) return null;

  const progress = calculateProgress(editedStockTake);
  const nextStatus = getNextStatus(editedStockTake.status);
  const previousStatus = getPreviousStatus(editedStockTake.status);

  // Update counted quantity
  const handleUpdateCount = (itemId: string, value: string) => {
    const numValue = value === "" ? null : parseInt(value, 10);
    if (numValue !== null && (isNaN(numValue) || numValue < 0)) return;

    const updatedItems = editedStockTake.items.map((item) => {
      if (item.id === itemId) {
        const variance = numValue !== null ? numValue - item.systemQuantity : null;
        return {
          ...item,
          countedQuantity: numValue,
          variance,
          countedBy: numValue !== null ? "Current User" : undefined,
          countedAt: numValue !== null ? new Date() : undefined,
        };
      }
      return item;
    });

    const countedItems = updatedItems.filter((item) => item.countedQuantity !== null).length;
    const varianceCount = updatedItems.filter(
      (item) => item.variance !== null && item.variance !== 0
    ).length;

    setEditedStockTake({
      ...editedStockTake,
      items: updatedItems,
      countedItems,
      varianceCount,
    });
    setHasUnsavedChanges(true);
  };

  // Update notes
  const handleUpdateNotes = (itemId: string, notes: string) => {
    const updatedItems = editedStockTake.items.map((item) =>
      item.id === itemId ? { ...item, notes: notes || undefined } : item
    );

    setEditedStockTake({
      ...editedStockTake,
      items: updatedItems,
    });
    setHasUnsavedChanges(true);
  };

  // Save stock take
  const handleSave = () => {
    const savedStockTake = {
      ...editedStockTake,
      lastSavedAt: new Date(),
      lastSavedBy: "Current User",
    };
    onSave(savedStockTake);
    setEditedStockTake(savedStockTake);
    setHasUnsavedChanges(false);
  };

  // Change status
  const handleStatusChange = (newStatus: StockTakeStatus) => {
    setPendingStatus(newStatus);
    setShowStatusChangeDialog(true);
  };

  const confirmStatusChange = () => {
    if (!pendingStatus) return;

    let updatedStockTake = {
      ...editedStockTake,
      status: pendingStatus,
      lastSavedAt: new Date(),
      lastSavedBy: "Current User",
    };

    // Set timestamps based on status
    if (pendingStatus === "In Progress" && !updatedStockTake.startedAt) {
      updatedStockTake.startedAt = new Date();
    } else if (pendingStatus === "Complete" && !updatedStockTake.completedAt) {
      updatedStockTake.completedAt = new Date();
    }

    onSave(updatedStockTake);
    setEditedStockTake(updatedStockTake);
    setHasUnsavedChanges(false);
    setShowStatusChangeDialog(false);
    setPendingStatus(null);
    toast.success(`Stock take moved to ${pendingStatus}`);
  };

  // Get status message for dialog
  const getStatusChangeMessage = () => {
    if (!pendingStatus) return "";

    switch (pendingStatus) {
      case "In Progress":
        return "This will mark the stock take as actively being counted. You can continue entering counts.";
      case "Review":
        return "This will move the stock take to review stage. All items should be counted before proceeding.";
      case "Complete":
        return "This will finalise the stock take. No further changes can be made after completion.";
      default:
        return "";
    }
  };

  // Check if can proceed to next status
  const canProceedToNext = () => {
    if (nextStatus === "Review") {
      // All items should be counted
      return editedStockTake.countedItems === editedStockTake.totalItems;
    }
    return true;
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-[900px] p-0 flex flex-col">
          {/* Header */}
          <SheetHeader className="bg-muted p-6 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <SheetTitle className="text-2xl font-semibold">
                  {editedStockTake.reference}
                </SheetTitle>
                <SheetDescription className="mt-1">
                  {editedStockTake.description}
                </SheetDescription>
              </div>
              <Badge className={getStatusColor(editedStockTake.status)}>
                {editedStockTake.status}
              </Badge>
            </div>

            {/* Progress */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-mono font-medium">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {editedStockTake.countedItems} of {editedStockTake.totalItems} items counted
                </span>
                {editedStockTake.varianceCount > 0 && (
                  <span className="text-destructive font-medium">
                    {editedStockTake.varianceCount} variances
                  </span>
                )}
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-4 pt-2 text-sm">
              <div>
                <span className="text-muted-foreground">Type:</span>
                <span className="ml-2">
                  <Badge className={getTypeColor(editedStockTake.type)}>
                    {editedStockTake.type}
                  </Badge>
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Warehouse:</span>
                <span className="ml-2">
                  {editedStockTake.warehouse ? (
                    <span className="font-medium">{editedStockTake.warehouse}</span>
                  ) : (
                    <Badge className="bg-purple-50 text-purple-700 border-purple-200">
                      All Warehouses
                    </Badge>
                  )}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Created:</span>
                <span className="ml-2 font-medium">
                  {formatDateSafe(editedStockTake.createdAt)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Created By:</span>
                <span className="ml-2 font-medium">
                  {editedStockTake.isSystemGenerated ? "System" : editedStockTake.createdBy}
                </span>
              </div>
              {editedStockTake.lastSavedAt && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Last saved:</span>
                  <span className="ml-2 font-medium">
                    {formatDateTimeSafe(editedStockTake.lastSavedAt)} by{" "}
                    {editedStockTake.lastSavedBy}
                  </span>
                </div>
              )}
            </div>

            {hasUnsavedChanges && (
              <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-3">
                <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  You have unsaved changes
                </p>
              </div>
            )}

            {editedStockTake.status === "Review" && (
              <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  This stock take is in review. All data is read-only. You can only complete or export the stock take.
                </p>
              </div>
            )}

            {editedStockTake.status === "Scheduled" && editedStockTake.isSystemGenerated && (
              <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-3">
                <p className="text-sm text-purple-800 dark:text-purple-200 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  This is a system-generated Cycle Count. {editedStockTake.totalItems} items have been pre-assigned based on ABC classification ({editedStockTake.abcClass}-Class). Counting will begin on {editedStockTake.nextScheduledDate && formatDateTimeSafe(editedStockTake.nextScheduledDate)}.
                </p>
              </div>
            )}
          </SheetHeader>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Filters - Hide for Scheduled stock takes (except system-generated Cycle Counts) */}
            {!(editedStockTake.status === "Scheduled" && !editedStockTake.isSystemGenerated) && (
              <div className="flex items-center gap-4 flex-wrap">
                <div className="relative w-[300px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <Select value={varianceFilter} onValueChange={setVarianceFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter items" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Items</SelectItem>
                    <SelectItem value="uncounted">Uncounted</SelectItem>
                    <SelectItem value="counted">Counted</SelectItem>
                    <SelectItem value="with-variance">With Variance</SelectItem>
                  </SelectContent>
                </Select>

                {(searchQuery || varianceFilter !== "all") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchQuery("");
                      setVarianceFilter("all");
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            )}

            {/* Items Table */}
            <div className="rounded-lg border">
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="font-semibold">Stock Code</TableHead>
                      <TableHead className="font-semibold">Description</TableHead>
                      <TableHead className="font-semibold">Location</TableHead>
                      <TableHead className="font-semibold text-right">System Qty</TableHead>
                      <TableHead className="font-semibold text-right">Counted Qty</TableHead>
                      <TableHead className="font-semibold text-right">Variance</TableHead>
                      <TableHead className="font-semibold">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.length > 0 ? (
                      filteredItems.map((item, index) => (
                        <TableRow key={item.id} className={index % 2 === 0 ? "bg-muted/30" : ""}>
                          <TableCell className="font-mono font-medium">
                            {item.stockCode}
                          </TableCell>
                          <TableCell>{item.stockDescription}</TableCell>
                          <TableCell className="font-mono">{item.location}</TableCell>
                          <TableCell className="text-right font-mono">
                            {item.systemQuantity}
                          </TableCell>
                          <TableCell className="text-right">
                            {editedStockTake.status !== "Complete" && editedStockTake.status !== "Review" && !(editedStockTake.status === "Scheduled" && editedStockTake.isSystemGenerated) ? (
                              <Input
                                type="number"
                                min="0"
                                value={item.countedQuantity ?? ""}
                                onChange={(e) => handleUpdateCount(item.id, e.target.value)}
                                className="w-24 text-right font-mono"
                                placeholder="Count"
                              />
                            ) : (
                              <span className="font-mono">{item.countedQuantity ?? "-"}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.variance !== null ? (
                              <span
                                className={`font-mono font-medium ${
                                  item.variance > 0
                                    ? "text-green-600"
                                    : item.variance < 0
                                    ? "text-red-600"
                                    : ""
                                }`}
                              >
                                {item.variance > 0 ? "+" : ""}
                                {item.variance}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {editedStockTake.status !== "Complete" && editedStockTake.status !== "Review" && !(editedStockTake.status === "Scheduled" && editedStockTake.isSystemGenerated) ? (
                              <Input
                                type="text"
                                value={item.notes ?? ""}
                                onChange={(e) => handleUpdateNotes(item.id, e.target.value)}
                                className="w-full"
                                placeholder="Add notes"
                              />
                            ) : (
                              <span className="text-sm">{item.notes || "-"}</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12">
                          {editedStockTake.status === "Scheduled" && !editedStockTake.isSystemGenerated ? (
                            <div className="flex flex-col items-center gap-3">
                              <AlertCircle className="h-12 w-12 text-muted-foreground/50" />
                              <div className="space-y-1">
                                <p className="text-sm font-medium text-muted-foreground">
                                  This stock take is scheduled
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {editedStockTake.totalItems} items will be counted when this stock take starts
                                </p>
                                {editedStockTake.nextScheduledDate && (
                                  <p className="text-xs text-muted-foreground">
                                    Next run: {formatDateTimeSafe(editedStockTake.nextScheduledDate)}
                                  </p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <p className="text-muted-foreground">No items found</p>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </div>

            {/* Notes */}
            {editedStockTake.notes && (
              <div className="space-y-2">
                <Label>Stock Take Notes</Label>
                <div className="rounded-lg bg-muted p-3 text-sm">
                  {editedStockTake.notes}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <SheetFooter className="bg-muted p-4 border-t flex-row justify-between gap-2">
            {/* Hide all actions for Scheduled stock takes (except system-generated Cycle Counts) */}
            {!(editedStockTake.status === "Scheduled" && !editedStockTake.isSystemGenerated) ? (
              <>
                <div className="flex items-center gap-2">
                  {/* Export - Show for all except Scheduled non-system stock takes */}
                  {!(editedStockTake.status === "Scheduled" && editedStockTake.isSystemGenerated) && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onExport(editedStockTake, "csv")}
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                        Export CSV
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onExport(editedStockTake, "excel")}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Export Excel
                      </Button>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {/* Show info for Scheduled Cycle Counts */}
                  {editedStockTake.status === "Scheduled" && editedStockTake.isSystemGenerated ? (
                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      <span>
                        {editedStockTake.totalItems} items assigned. Counting starts on{" "}
                        {editedStockTake.nextScheduledDate && formatDateTimeSafe(editedStockTake.nextScheduledDate)}
                      </span>
                    </div>
                  ) : (
                    <>
                      {/* Previous Status - Only show if NOT in Review or Complete */}
                      {previousStatus && editedStockTake.status !== "Complete" && editedStockTake.status !== "Review" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(previousStatus)}
                        >
                          <ChevronLeft className="h-4 w-4 mr-2" />
                          Back to {previousStatus}
                        </Button>
                      )}

                      {/* Save - Only show if NOT in Review or Complete */}
                      {editedStockTake.status !== "Complete" && editedStockTake.status !== "Review" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSave}
                          disabled={!hasUnsavedChanges}
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </Button>
                      )}

                      {/* Next Status */}
                      {nextStatus && (
                        <Button
                          size="sm"
                          onClick={() => handleStatusChange(nextStatus)}
                          disabled={!canProceedToNext()}
                        >
                          {nextStatus === "Complete" ? (
                            <>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Complete
                            </>
                          ) : (
                            <>
                              {nextStatus}
                              <ChevronRight className="h-4 w-4 ml-2" />
                            </>
                          )}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="w-full text-center text-sm text-muted-foreground py-2">
                This stock take will be available for counting when it starts on{" "}
                {editedStockTake.nextScheduledDate && formatDateTimeSafe(editedStockTake.nextScheduledDate)}
              </div>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Status Change Confirmation Dialog */}
      <AlertDialog open={showStatusChangeDialog} onOpenChange={setShowStatusChangeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Status to {pendingStatus}?</AlertDialogTitle>
            <AlertDialogDescription>
              {getStatusChangeMessage()}
              {hasUnsavedChanges && (
                <p className="mt-2 text-yellow-600 dark:text-yellow-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  You have unsaved changes. They will be saved automatically.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStatusChange}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
